import type Store from '@ember-data/store';
import type ModelRegistry from '@ember-data/types/registries/model';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { EmberChangeset } from 'ember-changeset';
import type MedicationPresentation from 'parog-web/models/medication-presentation';
import gt from 'ember-truth-helpers/helpers/gt';
import t from 'ember-intl/helpers/t';
import { concat, fn } from '@ember/helper';
import LinkButton from 'ui-kit/components/link-button';
import { on } from '@ember/modifier';

const currentComorbChangesetKey = 'comorbidities';
const ignoredComorbChangesetKey = 'ignoredComorbidities';

interface Args {
  patient: ModelRegistry['patient'];
  changeset: EmberChangeset;
}

export default class PathologyDetectionComponent extends Component<Args> {
  @service icd10;
  @service declare store: Store;

  /**
   * This method is a fake pathology detection method used only in demo ENV
   */
  get detectedPathologies(): string[] {
    let atcFoundDate: Date | null = null;
    const hasDeliveries =
      this.args.patient.drugsAndDevicesRefundedJournalEvents &&
      this.args.patient.drugsAndDevicesRefundedJournalEvents.some(je =>
        je.prestations.some(prestation => {
          const medicationPresentation = this.store.peekRecord(
            'medication-presentation',
            prestation.sourceValue
          ) as MedicationPresentation;
          if (
            medicationPresentation.medicationSpecialty.medicationMolecule.atc.startsWith(
              'A10'
            )
          ) {
            // If we found the code for the second time at a different day
            if (
              atcFoundDate &&
              atcFoundDate.toDateString() != je.occurredAt.toDateString()
            ) {
              return true;
            }
            atcFoundDate = je.occurredAt;
          }
          return false;
        })
      );

    if (hasDeliveries) {
      return ['E10', 'E11'].filter(code => {
        return (
          !this.#currentSelectedComorbidities.includes(code) &&
          !this.#currentIgnoredComorbidities.includes(code)
        );
      });
    }

    return [];
  }

  get #changeset() {
    return this.args.changeset;
  }

  get #currentIgnoredComorbidities(): string[] {
    return this.#changeset.get(ignoredComorbChangesetKey) || [];
  }

  get #currentSelectedComorbidities(): string[] {
    return this.#changeset.get(currentComorbChangesetKey) || [];
  }

  addComorbidities = (): void => {
    this.#changeset.set(currentComorbChangesetKey, [
      ...this.#currentSelectedComorbidities,
      ...this.detectedPathologies
    ]);
  };

  addComorbidity = (code: string): void => {
    this.#changeset.set(currentComorbChangesetKey, [
      ...this.#currentSelectedComorbidities,
      code
    ]);
  };

  ignoreComorbidity = (code: string): void => {
    this.#changeset.set(ignoredComorbChangesetKey, [
      ...this.#currentIgnoredComorbidities,
      code
    ]);
  };

  <template>
    {{#if (gt this.detectedPathologies.length 0)}}
      <div
        class='ui segment show-shyone-on-hover'
        data-test-pathology-detection-section
      >
        <h5>Les pathologies suivantes ont été détectées à partir des données
          disponibles</h5>
        <ul class='ui list'>
          {{#each this.detectedPathologies as |pathology|}}
            <li data-test-illness-detected={{pathology}}>
              {{t (concat 'comorbidity.icd10.' pathology)}}

              <span data-tooltip='Ajouter aux comorbidités'>
                <LinkButton
                  class='item shyone'
                  data-test-add-illness={{pathology}}
                  {{on 'click' (fn this.addComorbidity pathology)}}
                >
                  <i class='ui add icon'></i>
                </LinkButton>
              </span>
              <span data-tooltip='Ignorer'>
                <LinkButton
                  class='item shyone'
                  data-test-ignore-illness={{pathology}}
                  {{on 'click' (fn this.ignoreComorbidity pathology)}}
                >
                  <i class='ui times icon'></i>
                </LinkButton>
              </span>
            </li>
          {{/each}}
        </ul>
        <button
          type='button'
          class='ui button'
          data-test-add-all-illness
          {{on 'click' this.addComorbidities}}
        >
          Ajouter toutes les pathologies détectées
        </button>
      </div>
    {{/if}}
  </template>
}
